import React, { useState } from "react";
import { useAuth } from "../components/context/AuthContext";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

export default function LoginPage() {
  const { login, register, resetPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isAdminRoute = location.pathname === "/admin";

  const [isLoginState, setIsLoginState] = useState("login"); // "login", "signup", "booking"
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    number: "",
    password: ""
  });
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Password reset specific states
  const [resetMode, setResetMode] = useState(null); // null, "active"
  const [resetEmail, setResetEmail] = useState("");
  const [resetPhone, setResetPhone] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Check Booking inline states
  const [searchBookingId, setSearchBookingId] = useState("");
  const [searchPhone, setSearchPhone] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchSearched, setSearchSearched] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrorMsg("");
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const handleStateChange = (stateVal) => {
    setIsLoginState(stateVal);
    setResetMode(null);
    setResetEmail("");
    setResetPhone("");
    setNewPassword("");
    setConfirmPassword("");
    setErrorMsg("");
    setSuccessMsg("");
    setFormData({ fullName: "", email: "", number: "", password: "" });
    setSearchBookingId("");
    setSearchPhone("");
    setSearchResults([]);
    setSearchSearched(false);
  };

  const handleSearchBookings = async (e) => {
    e.preventDefault();
    if (!searchBookingId.trim() || !searchPhone.trim()) {
      setErrorMsg("Both Booking ID and Phone Number are required.");
      return;
    }

    setSearchLoading(true);
    setSearchSearched(true);
    setSearchResults([]);
    setErrorMsg("");

    const targetBookingId = searchBookingId.trim().toUpperCase();
    const targetPhone = searchPhone.trim().replace(/\s+/g, "");
    let matchedResults = [];

    // Search in local database
    try {
      const localBookings = JSON.parse(localStorage.getItem("bookings") || "[]");
      const matched = localBookings.filter((b) => {
        const bId = (b.bookingId || b.id || "").toUpperCase();
        const bPhone = (b.number || b.phone || "").replace(/\s+/g, "");
        return bId === targetBookingId && bPhone === targetPhone;
      });
      matchedResults = matched.map((b) => ({
        bookingId: b.bookingId || b.id,
        roomType: b.roomType,
        checkInDate: b.checkInDate || b.checkIn,
        checkOutDate: b.checkOutDate || b.checkOut,
        status: b.status || "Pending",
      }));
    } catch (err) {
      console.warn("Error reading local bookings", err);
    }

    setSearchResults(matchedResults);
    setSearchLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");
    const { fullName, email, number, password } = formData;

    if (isAdminRoute) {
      if (!email || !password) { setErrorMsg("Admin email and password are required."); return; }
      setIsSubmitting(true);
      const res = await login(email, password);
      setIsSubmitting(false);
      if (res.success) { navigate("/admin-dashboard"); } else { setErrorMsg(res.error || "Incorrect manager credentials."); }
      return;
    }

    if (isLoginState === "signup") {
      if (!fullName || !email || !number || !password) { setErrorMsg("All registration fields are required."); return; }
      setIsSubmitting(true);
      const res = await register(fullName, email, number, password);
      setIsSubmitting(false);
      if (res.success) {
        setSuccessMsg("Account created successfully! Kindly log in.");
        setIsLoginState("login");
        setFormData({ ...formData, password: "" });
      } else { setErrorMsg(res.error); }
    } else {
      if (!email || !password) { setErrorMsg("Email and password are required."); return; }
      setIsSubmitting(true);
      const res = await login(email, password);
      setIsSubmitting(false);
      if (res.success) { navigate("/user-dashboard"); } else { setErrorMsg(res.error); }
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    if (!resetEmail || !resetPhone || !newPassword || !confirmPassword) {
      setErrorMsg("All fields are required.");
      return;
    }

    if (newPassword.length < 8) {
      setErrorMsg("Password must be at least 8 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMsg("Passwords do not match.");
      return;
    }

    setIsSubmitting(true);
    const res = await resetPassword(resetEmail, resetPhone, newPassword);
    setIsSubmitting(false);

    if (res.success) {
      setSuccessMsg(res.message || "Password reset successfully! You can now log in.");
      setResetMode(null);
      setResetEmail("");
      setResetPhone("");
      setNewPassword("");
      setConfirmPassword("");
      setFormData({ fullName: "", email: resetEmail, number: "", password: "" });
    } else {
      setErrorMsg(res.error || "Password reset failed. Please verify your details.");
    }
  };

  // ── ADMIN LOGIN VIEW ──
  if (isAdminRoute) {
    if (resetMode === "request") {
      return (
        <>
          <Helmet><title>Reset Manager Key – HariKunj Resort Jodhpur</title></Helmet>
          <div className="hk-auth-page">
            <div className="hk-auth-outer">
              <div className="hk-admin-card">
                <div className="hk-admin-lock-icon"><i className="fa fa-user-shield" /></div>
                <h3 className="hk-admin-heading">Reset Manager Key</h3>
                <p className="hk-admin-sub">Verify your identity with your registered email and phone number to set a new access key.</p>
                <div className="hk-divider-gold" />

                {errorMsg && <div className="hk-alert hk-alert-danger">{errorMsg}</div>}
                {successMsg && <div className="hk-alert hk-alert-success">{successMsg}</div>}

                <form onSubmit={handleResetSubmit} className="hk-auth-form">
                  <div className="hk-field">
                    <label className="hk-label">Authorized Admin Email</label>
                    <div className="hk-input-box">
                      <i className="fa fa-envelope hk-input-icon" />
                      <input type="email" placeholder="admin@harikunjresort.com" className="hk-input" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} required />
                    </div>
                  </div>
                  <div className="hk-field">
                    <label className="hk-label">Registered Phone Number</label>
                    <div className="hk-input-box">
                      <i className="fa fa-phone hk-input-icon" />
                      <input type="tel" placeholder="+91 9876543210" className="hk-input" value={resetPhone} onChange={(e) => setResetPhone(e.target.value)} required />
                    </div>
                  </div>
                  <div className="hk-field">
                    <label className="hk-label">New Secure Key</label>
                    <div className="hk-input-box">
                      <i className="fa fa-key hk-input-icon" />
                      <input type={showPassword ? "text" : "password"} placeholder="New Password (min 8 chars)" className="hk-input" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
                      <button type="button" className="hk-eye-btn" onClick={togglePasswordVisibility} tabIndex={-1}>
                         <i className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
                      </button>
                    </div>
                  </div>
                  <div className="hk-field">
                    <label className="hk-label">Confirm New Key</label>
                    <div className="hk-input-box">
                      <i className="fa fa-key hk-input-icon" />
                      <input type={showPassword ? "text" : "password"} placeholder="Confirm Password" className="hk-input" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
                    </div>
                  </div>
                  <button type="submit" disabled={isSubmitting} className="hk-submit-btn">
                    {isSubmitting && <span className="spinner-border spinner-border-sm me-2" />}
                    Verify & Reset Password
                  </button>
                  
                  <button type="button" className="hk-create-btn" style={{ borderColor: "rgba(255,255,255,0.15)", color: "rgba(250,246,240,0.7)", marginTop: "10px" }} onClick={() => handleStateChange("login")}>
                    ← Back to Authorization Hub
                  </button>
                </form>
              </div>
            </div>
          </div>
          <style>{pageStyles}</style>
        </>
      );
    }

    return (
      <>
        <Helmet><title>Resort Manager Authorization – HariKunj Resort Jodhpur</title></Helmet>
        <div className="hk-auth-page">
          <div className="hk-auth-outer">
            <div className="hk-admin-card">
              <div className="hk-admin-lock-icon"><i className="fa fa-shield-alt" /></div>
              <h3 className="hk-admin-heading">Manager Access Portal</h3>
              <p className="hk-admin-sub">Authorized Personnel Only. Enterprise credentials required.</p>
              <div className="hk-divider-gold" />

              {errorMsg && <div className="hk-alert hk-alert-danger">{errorMsg}</div>}
              {successMsg && <div className="hk-alert hk-alert-success">{successMsg}</div>}

              <form onSubmit={handleSubmit} className="hk-auth-form">
                <div className="hk-field">
                  <label className="hk-label">Admin Email Address</label>
                  <div className="hk-input-box">
                    <i className="fa fa-envelope hk-input-icon" />
                    <input type="email" name="email" placeholder="admin@harikunjresort.com" className="hk-input" value={formData.email} onChange={handleInputChange} required />
                  </div>
                </div>
                <div className="hk-field">
                  <label className="hk-label">Manager Secure Key</label>
                  <div className="hk-input-box">
                    <i className="fa fa-key hk-input-icon" />
                    <input type={showPassword ? "text" : "password"} name="password" placeholder="••••••••" className="hk-input" value={formData.password} onChange={handleInputChange} required />
                    <button type="button" className="hk-eye-btn" onClick={togglePasswordVisibility} tabIndex={-1}>
                      <i className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
                    </button>
                  </div>
                </div>
                <button type="button" className="hk-check-booking-link-btn" onClick={() => { setResetMode("request"); setResetEmail(formData.email); setErrorMsg(""); setSuccessMsg(""); }} style={{
                  background: "none",
                  border: "none",
                  color: "#FEA116",
                  fontWeight: "700",
                  fontSize: "0.82rem",
                  marginTop: "-10px",
                  marginBottom: "10px",
                  cursor: "pointer",
                  fontFamily: "'Poppins', sans-serif",
                  textAlign: "right",
                  alignSelf: "flex-end"
                }}>
                  Forgot Secure Key?
                </button>
                <button type="submit" disabled={isSubmitting} className="hk-submit-btn">
                  {isSubmitting && <span className="spinner-border spinner-border-sm me-2" />}
                  Authorize & Enter Hub
                </button>
              </form>
            </div>
          </div>
        </div>
        <style>{pageStyles}</style>
      </>
    );
  }

  // ── GUEST LOGIN & SIGNUP SPLIT VIEW ──
  const isLogin = isLoginState === "login" && !resetMode;
  const isSignup = isLoginState === "signup" && !resetMode;
  const isBooking = isLoginState === "booking" && !resetMode;

  let pageTitle = "Guest Suite Access – Jodhpur Stay";
  if (isSignup) {
    pageTitle = "Join HariKunj Guest List – Jodhpur Stay";
  } else if (isBooking) {
    pageTitle = "Track Your Reservation – Jodhpur Stay";
  } else if (resetMode === "request") {
    pageTitle = "Recover Heritage Key – Jodhpur Stay";
  } else if (resetMode === "verify") {
    pageTitle = "Verify Identity Token – Jodhpur Stay";
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <div className="hk-auth-page">
        <div className="hk-auth-outer">
          <div className={`hk-split-card ${isSignup ? "hk-split-card--signup" : "hk-split-card--login"}`}>

            {isLogin && (
              <>
                {/* ── LEFT: Branding Bubble ── */}
                <div className="hk-brand-bubble hk-brand-bubble--left">
                  <Link to="/" className="hk-back-arrow"><i className="fa fa-arrow-left" /></Link>
                  <div className="hk-brand-top">
                    <Link to="/" className="hk-logo-link">
                      <span className="hk-logo-main">HariKunj</span>
                      <span className="hk-logo-sub">RESORT JODHPUR</span>
                    </Link>
                  </div>
                  <div className="hk-brand-center">
                    <div className="hk-star-icon"><i className="fa fa-crown" /></div>
                    <h3 className="hk-brand-title">Welcome Back</h3>
                    <p className="hk-brand-desc">Access top resort suites, check-in countdowns, and direct reservation pipelines.</p>
                  </div>
                  <div className="hk-brand-bottom">
                    <span className="hk-switch-label">NEW HERE?</span>
                    <button type="button" className="hk-switch-btn" onClick={() => handleStateChange("signup")}>SIGN UP</button>
                  </div>
                </div>

                {/* ── RIGHT: Login Form ── */}
                <div className="hk-form-side">
                  <h3 className="hk-form-heading">Guest Suite Log In</h3>
                  <div className="hk-divider-gold" />

                  {errorMsg && <div className="hk-alert hk-alert-danger">{errorMsg}</div>}
                  {successMsg && <div className="hk-alert hk-alert-success">{successMsg}</div>}

                  <form onSubmit={handleSubmit} className="hk-auth-form">
                    <div className="hk-field">
                      <label className="hk-label">Email Address</label>
                      <div className="hk-input-box">
                        <i className="fa fa-envelope hk-input-icon" />
                        <input type="email" name="email" placeholder="you@example.com" className="hk-input" value={formData.email} onChange={handleInputChange} required />
                      </div>
                    </div>
                    <div className="hk-field">
                      <label className="hk-label">Password</label>
                      <div className="hk-input-box">
                        <i className="fa fa-lock hk-input-icon" />
                        <input type={showPassword ? "text" : "password"} name="password" placeholder="••••••••" className="hk-input" value={formData.password} onChange={handleInputChange} required />
                        <button type="button" className="hk-eye-btn" onClick={togglePasswordVisibility} tabIndex={-1}>
                          <i className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
                        </button>
                      </div>
                    </div>
                    <button type="button" className="hk-check-booking-link-btn" onClick={() => { setResetMode("request"); setResetEmail(formData.email); setErrorMsg(""); setSuccessMsg(""); }} style={{
                      background: "none",
                      border: "none",
                      color: "#FEA116",
                      fontWeight: "700",
                      fontSize: "0.82rem",
                      marginTop: "-10px",
                      cursor: "pointer",
                      fontFamily: "'Poppins', sans-serif",
                      textAlign: "right",
                      alignSelf: "flex-end"
                    }}>
                      Forgot Password?
                    </button>
                    <button type="submit" disabled={isSubmitting} className="hk-submit-btn">
                      {isSubmitting && <span className="spinner-border spinner-border-sm me-2" />}
                      Login →
                    </button>
                    
                    <button type="button" className="hk-check-booking-link-btn" onClick={() => handleStateChange("booking")} style={{
                      background: "none",
                      border: "none",
                      color: "#FEA116",
                      fontWeight: "700",
                      fontSize: "0.85rem",
                      marginTop: "4px",
                      cursor: "pointer",
                      fontFamily: "'Poppins', sans-serif"
                    }}>
                      <i className="fas fa-search" style={{ marginRight: "6px" }} />
                      Track Booking Status without Account
                    </button>

                    <button type="button" className="hk-create-btn" onClick={() => handleStateChange("signup")}>
                      Create your account
                    </button>
                  </form>

                  <div className="hk-mobile-switch d-md-none">
                    <span>New here? </span>
                    <button type="button" onClick={() => handleStateChange("signup")}>Create an account</button>
                  </div>
                </div>
              </>
            )}

            {isSignup && (
              <>
                {/* ── LEFT: Signup Form ── */}
                <div className="hk-form-side hk-form-side--signup order-2 order-md-1">
                  <Link to="/" className="hk-back-arrow-form d-md-none"><i className="fa fa-arrow-left" /></Link>
                  <h3 className="hk-form-heading">Create Account</h3>
                  <div className="hk-divider-gold" />

                  {errorMsg && <div className="hk-alert hk-alert-danger">{errorMsg}</div>}

                  <form onSubmit={handleSubmit} className="hk-auth-form">
                    <div className="hk-field">
                      <label className="hk-label">Full Name</label>
                      <div className="hk-input-box">
                        <i className="fa fa-user hk-input-icon" />
                        <input type="text" name="fullName" placeholder="John Doe" className="hk-input" value={formData.fullName} onChange={handleInputChange} required />
                      </div>
                    </div>
                    <div className="hk-field">
                      <label className="hk-label">Email Address <span style={{color:"#e74c3c"}}>*</span></label>
                      <div className="hk-input-box">
                        <i className="fa fa-envelope hk-input-icon" />
                        <input type="email" name="email" placeholder="you@example.com" className="hk-input" value={formData.email} onChange={handleInputChange} required />
                      </div>
                    </div>
                    <div className="hk-field">
                      <label className="hk-label">Phone Number</label>
                      <div className="hk-input-box">
                        <span className="hk-country-code">+91</span>
                        <input type="text" name="number" placeholder="9876543210" className="hk-input hk-input--phone" value={formData.number} onChange={handleInputChange} required />
                        <i className="fa fa-phone hk-phone-icon" />
                      </div>
                    </div>
                    <div className="hk-field">
                      <label className="hk-label">Security Password</label>
                      <div className="hk-input-box">
                        <i className="fa fa-lock hk-input-icon" />
                        <input type={showPassword ? "text" : "password"} name="password" placeholder="••••••••" className="hk-input" value={formData.password} onChange={handleInputChange} required />
                        <button type="button" className="hk-eye-btn" onClick={togglePasswordVisibility} tabIndex={-1}>
                          <i className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
                        </button>
                      </div>
                    </div>
                    <button type="submit" disabled={isSubmitting} className="hk-submit-btn">
                      {isSubmitting && <span className="spinner-border spinner-border-sm me-2" />}
                      Continue →
                    </button>
                  </form>

                  <div className="hk-footer-switch">
                    <span>Already have an account? </span>
                    <button type="button" onClick={() => handleStateChange("login")}>Login to your account</button>
                  </div>
                </div>

                {/* ── RIGHT: Branding Bubble ── */}
                <div className="hk-brand-bubble hk-brand-bubble--right order-1 order-md-2">
                  <Link to="/" className="hk-back-arrow d-none d-md-flex"><i className="fa fa-arrow-left" /></Link>
                  <div className="hk-brand-top">
                    <Link to="/" className="hk-logo-link">
                      <span className="hk-logo-main">HariKunj</span>
                      <span className="hk-logo-sub">RESORT JODHPUR</span>
                    </Link>
                  </div>
                  <div className="hk-brand-center">
                    <div className="hk-star-icon"><i className="fa fa-crown" /></div>
                    <h3 className="hk-brand-title">Join HariKunj</h3>
                    <p className="hk-brand-desc">Build a stunning heritage travel portfolio, earn custom guest packages, and connect with Jodhpur's finest resort crew.</p>
                  </div>
                  <div className="hk-brand-bottom">
                    <span className="hk-switch-label">WELCOME BACK!</span>
                    <button type="button" className="hk-switch-btn" onClick={() => handleStateChange("login")}>SIGN IN</button>
                  </div>
                </div>
              </>
            )}

            {isBooking && (
              <>
                {/* ── LEFT: Branding Bubble ── */}
                <div className="hk-brand-bubble hk-brand-bubble--left">
                  <Link to="/" className="hk-back-arrow"><i className="fa fa-arrow-left" /></Link>
                  <div className="hk-brand-top">
                    <Link to="/" className="hk-logo-link">
                      <span className="hk-logo-main">HariKunj</span>
                      <span className="hk-logo-sub">RESORT JODHPUR</span>
                    </Link>
                  </div>
                  <div className="hk-brand-center">
                    <div className="hk-star-icon"><i className="fa fa-search" /></div>
                    <h3 className="hk-brand-title">Track Stay</h3>
                    <p className="hk-brand-desc">Verify your reservation details, check-in status, and room allocations in real-time.</p>
                  </div>
                  <div className="hk-brand-bottom">
                    <span className="hk-switch-label">LOG IN TO SUITE?</span>
                    <button type="button" className="hk-switch-btn" onClick={() => handleStateChange("login")}>SIGN IN</button>
                  </div>
                </div>

                {/* ── RIGHT: Check Booking Form ── */}
                <div className="hk-form-side">
                  <h3 className="hk-form-heading">Check Your Booking</h3>
                  <div className="hk-divider-gold" />
                  <p style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: "0.85rem",
                    color: "#6B7A8D",
                    marginBottom: "18px",
                    lineHeight: "1.6"
                  }}>
                    Enter your exact Booking ID and registered Phone Number to check your reservation status.
                  </p>

                  <form onSubmit={handleSearchBookings} className="hk-auth-form">
                    <div className="hk-field">
                      <label className="hk-label">Booking ID</label>
                      <div className="hk-input-box">
                        <i className="fa fa-receipt hk-input-icon" />
                        <input 
                          type="text" 
                          placeholder="e.g. HKB-123456" 
                          className="hk-input" 
                          value={searchBookingId} 
                          onChange={(e) => setSearchBookingId(e.target.value)} 
                          required 
                        />
                      </div>
                    </div>
                    <div className="hk-field">
                      <label className="hk-label">Phone Number</label>
                      <div className="hk-input-box">
                        <i className="fa fa-phone hk-input-icon" />
                        <input 
                          type="tel" 
                          placeholder="Registered 10-digit Phone Number" 
                          className="hk-input" 
                          value={searchPhone} 
                          onChange={(e) => setSearchPhone(e.target.value)} 
                          required 
                        />
                      </div>
                    </div>
                    <button type="submit" disabled={searchLoading} className="hk-submit-btn">
                      {searchLoading && <span className="spinner-border spinner-border-sm me-2" />}
                      Verify & Check Booking →
                    </button>
                  </form>

                  {/* Results Display Area */}
                  <div className="hk-booking-results-area" style={{ marginTop: "20px" }}>
                    {searchLoading ? (
                      <div style={{ textAlign: "center", padding: "20px 0" }}>
                        <div className="spinner-border text-primary" role="status"></div>
                        <p style={{ marginTop: "10px", fontSize: "0.85rem", color: "#3D4F6A" }}>Verifying reservation...</p>
                      </div>
                    ) : searchSearched && searchResults.length > 0 ? (
                      <div className="hk-booking-results-list" style={{ maxHeight: "220px", overflowY: "auto", paddingRight: "5px" }}>
                        <h4 style={{ fontFamily: "'Cinzel', serif", fontSize: "0.95rem", color: "#0B1D37", marginBottom: "12px", fontWeight: "700" }}>
                          Found Matching Reservation
                        </h4>
                        {searchResults.map((booking, idx) => (
                          <div key={booking.bookingId || idx} className="hk-booking-result-card" style={{
                            background: "rgba(11, 36, 71, 0.04)",
                            border: "1.5px solid rgba(212, 175, 55, 0.25)",
                            borderRadius: "14px",
                            padding: "12px 14px",
                            marginBottom: "10px",
                            fontSize: "0.82rem"
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", borderBottom: "1px solid rgba(11, 36, 71, 0.08)", paddingBottom: "6px" }}>
                              <span style={{ fontWeight: "700", color: "#FEA116" }}>{booking.roomType}</span>
                              <span className={`badge ${
                                (booking.status || "").toLowerCase() === "confirmed" ? "bg-success" : "bg-warning text-dark"
                              }`}>{booking.status || "Pending"}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                              <span style={{ color: "#6B7A8D" }}>Booking Reference:</span>
                              <span style={{ fontWeight: "600", color: "#0B1D37" }}>{booking.bookingId}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                              <span style={{ color: "#6B7A8D" }}>Check-In Date:</span>
                              <span style={{ fontWeight: "600", color: "#0B1D37" }}>{booking.checkInDate}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                              <span style={{ color: "#6B7A8D" }}>Check-Out Date:</span>
                              <span style={{ fontWeight: "600", color: "#0B1D37" }}>{booking.checkOutDate}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : searchSearched ? (
                      <div style={{
                        textAlign: "center",
                        padding: "15px",
                        background: "rgba(231, 76, 60, 0.05)",
                        border: "1px solid rgba(231, 76, 60, 0.15)",
                        borderRadius: "14px",
                        color: "#c0392b"
                      }}>
                        <h5 style={{ fontSize: "0.9rem", fontWeight: "700", marginBottom: "6px" }}>No Match Found</h5>
                        <p style={{ fontSize: "0.78rem", margin: "0", color: "#555" }}>
                          No reservation matched the provided Booking ID and Phone Number. Please check your confirmation email.
                        </p>
                      </div>
                    ) : null}
                  </div>

                  <button type="button" className="hk-create-btn" style={{ marginTop: "16px" }} onClick={() => handleStateChange("login")}>
                    ← Back to Sign In
                  </button>
                </div>
              </>
            )}

            {resetMode === "request" && (
              <>
                {/* ── LEFT: Branding Bubble ── */}
                <div className="hk-brand-bubble hk-brand-bubble--left">
                  <Link to="/" className="hk-back-arrow"><i className="fa fa-arrow-left" /></Link>
                  <div className="hk-brand-top">
                    <Link to="/" className="hk-logo-link">
                      <span className="hk-logo-main">HariKunj</span>
                      <span className="hk-logo-sub">RESORT JODHPUR</span>
                    </Link>
                  </div>
                  <div className="hk-brand-center">
                    <div className="hk-star-icon"><i className="fa fa-key" /></div>
                    <h3 className="hk-brand-title">Recover Key</h3>
                    <p className="hk-brand-desc">Regain entry to your customized heritage travel profile and booking pipeline.</p>
                  </div>
                  <div className="hk-brand-bottom">
                    <span className="hk-switch-label">KNOW YOUR CREDENTIALS?</span>
                    <button type="button" className="hk-switch-btn" onClick={() => handleStateChange("login")}>SIGN IN</button>
                  </div>
                </div>

                {/* ── RIGHT: Reset Form ── */}
                <div className="hk-form-side">
                  <h3 className="hk-form-heading">Reset Password</h3>
                  <div className="hk-divider-gold" />
                  <p style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: "0.85rem",
                    color: "#6B7A8D",
                    marginBottom: "18px",
                    lineHeight: "1.6"
                  }}>
                    Verify your identity with your registered email and phone number to set a new password.
                  </p>

                  {errorMsg && <div className="hk-alert hk-alert-danger">{errorMsg}</div>}
                  {successMsg && <div className="hk-alert hk-alert-success">{successMsg}</div>}

                  <form onSubmit={handleResetSubmit} className="hk-auth-form">
                    <div className="hk-field">
                      <label className="hk-label">Email Address</label>
                      <div className="hk-input-box">
                        <i className="fa fa-envelope hk-input-icon" />
                        <input 
                          type="email" 
                          placeholder="you@example.com" 
                          className="hk-input" 
                          value={resetEmail} 
                          onChange={(e) => setResetEmail(e.target.value)} 
                          required 
                        />
                      </div>
                    </div>
                    <div className="hk-field">
                      <label className="hk-label">Phone Number</label>
                      <div className="hk-input-box">
                        <span className="hk-country-code">+91</span>
                        <input 
                          type="text" 
                          placeholder="9876543210" 
                          className="hk-input hk-input--phone" 
                          value={resetPhone} 
                          onChange={(e) => setResetPhone(e.target.value)} 
                          required 
                        />
                        <i className="fa fa-phone hk-phone-icon" />
                      </div>
                    </div>
                    <div className="hk-field">
                      <label className="hk-label">New Password</label>
                      <div className="hk-input-box">
                        <i className="fa fa-lock hk-input-icon" />
                        <input 
                          type={showPassword ? "text" : "password"} 
                          placeholder="••••••••" 
                          className="hk-input" 
                          value={newPassword} 
                          onChange={(e) => setNewPassword(e.target.value)} 
                          required 
                        />
                        <button type="button" className="hk-eye-btn" onClick={togglePasswordVisibility} tabIndex={-1}>
                          <i className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
                        </button>
                      </div>
                    </div>
                    <div className="hk-field">
                      <label className="hk-label">Confirm New Password</label>
                      <div className="hk-input-box">
                        <i className="fa fa-lock hk-input-icon" />
                        <input 
                          type={showPassword ? "text" : "password"} 
                          placeholder="••••••••" 
                          className="hk-input" 
                          value={confirmPassword} 
                          onChange={(e) => setConfirmPassword(e.target.value)} 
                          required 
                        />
                      </div>
                    </div>
                    <button type="submit" disabled={isSubmitting} className="hk-submit-btn">
                      {isSubmitting && <span className="spinner-border spinner-border-sm me-2" />}
                      Reset Password →
                    </button>
                  </form>

                  <button type="button" className="hk-create-btn" style={{ marginTop: "16px" }} onClick={() => handleStateChange("login")}>
                    ← Back to Sign In
                  </button>
                </div>
              </>
            )}

          </div>
        </div>
      </div>

      <style>{pageStyles}</style>
    </>
  );
}


// ═══════════════════════════════════════════════════
// ─── STYLES (Matching DojoWorks reference images) ─
// ═══════════════════════════════════════════════════

const pageStyles = `

/* ── Page wrapper ── */
.hk-auth-page {
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(145deg, #E8EAF0 0%, #D6DAE4 40%, #C8CEDb 100%);
  padding: 10px 16px 30px;
  box-sizing: border-box;
}

.hk-auth-outer {
  width: 100%;
  max-width: 1020px;
}

/* ── Split card container ── */
.hk-split-card {
  display: flex;
  flex-direction: row;
  width: 100%;
  min-height: 600px;
  border-radius: 28px;
  overflow: hidden;
  background: #FBFAF8;
  box-shadow:
    0 30px 80px rgba(5, 14, 26, 0.12),
    0 8px 24px rgba(5, 14, 26, 0.08);
  border: 1px solid rgba(212, 175, 55, 0.15);
  position: relative;
}

/* ════════════════════════════════════════════
   BRANDING BUBBLE PANEL (the curved side)
   ════════════════════════════════════════════ */
.hk-brand-bubble {
  flex: 0 0 42%;
  max-width: 42%;
  background: linear-gradient(160deg, #0D2A54 0%, #091A38 45%, #050E1A 100%);
  color: #FAF6F0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  padding: 32px 28px 28px;
  position: relative;
  z-index: 2;
}

/* Smooth organic curve on right edge (Login state) */
.hk-brand-bubble--left {
  border-radius: 28px 180px 180px 28px;
}

/* Smooth organic curve on left edge (Signup state) */
.hk-brand-bubble--right {
  border-radius: 180px 28px 28px 180px;
}

/* Subtle inner glow overlay */
.hk-brand-bubble::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: inherit;
  background: radial-gradient(ellipse at 30% 20%, rgba(30, 62, 114, 0.4) 0%, transparent 60%);
  pointer-events: none;
}

/* Back arrow button */
.hk-back-arrow {
  position: absolute;
  top: 20px;
  left: 20px;
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  text-decoration: none;
  font-size: 0.85rem;
  transition: all 0.25s ease;
  z-index: 3;
}
.hk-back-arrow:hover {
  background: rgba(254, 161, 22, 0.2);
  border-color: rgba(254, 161, 22, 0.5);
  color: #FEA116;
}

.hk-back-arrow-form {
  position: absolute;
  top: 16px;
  left: 16px;
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background: rgba(11, 29, 55, 0.06);
  border: 1px solid rgba(11, 29, 55, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0B1D37;
  text-decoration: none;
  font-size: 0.85rem;
  transition: all 0.25s ease;
  z-index: 3;
}

/* Logo in bubble */
.hk-brand-top {
  text-align: center;
  z-index: 2;
}
.hk-logo-link {
  text-decoration: none;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.hk-logo-main {
  font-family: 'Cinzel Decorative', serif;
  font-weight: 800;
  font-size: 1.5rem;
  color: #FEA116;
  letter-spacing: 2px;
  text-shadow: 0 2px 8px rgba(254, 161, 22, 0.3);
}
.hk-logo-sub {
  font-family: 'Poppins', sans-serif;
  font-size: 0.62rem;
  font-weight: 700;
  color: rgba(250, 246, 240, 0.7);
  letter-spacing: 5px;
  margin-top: 2px;
}

/* Center content in bubble */
.hk-brand-center {
  text-align: center;
  z-index: 2;
  padding: 0 10px;
}

/* Star/Crown icon bubble */
.hk-star-icon {
  width: 64px;
  height: 64px;
  border-radius: 18px;
  background: rgba(254, 161, 22, 0.12);
  border: 1.5px solid rgba(254, 161, 22, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 1.5rem;
  color: #FEA116;
  box-shadow: 0 8px 24px rgba(254, 161, 22, 0.15);
}

.hk-brand-title {
  font-family: 'Cinzel', serif;
  font-weight: 700;
  font-size: 1.75rem;
  color: #FEA116;
  margin-bottom: 12px;
  letter-spacing: 0.5px;
}

.hk-brand-desc {
  font-family: 'Poppins', sans-serif;
  font-size: 0.85rem;
  color: rgba(250, 246, 240, 0.65);
  line-height: 1.7;
  max-width: 260px;
  margin: 0 auto;
}

/* Bottom switcher */
.hk-brand-bottom {
  text-align: center;
  z-index: 2;
}

.hk-switch-label {
  font-family: 'Poppins', sans-serif;
  font-size: 0.7rem;
  letter-spacing: 2px;
  color: rgba(250, 246, 240, 0.45);
  font-weight: 700;
  display: block;
  margin-bottom: 10px;
}

.hk-switch-btn {
  background: transparent;
  border: 1.5px solid rgba(254, 161, 22, 0.6);
  color: #FEA116;
  font-family: 'Poppins', sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 2px;
  padding: 10px 36px;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.hk-switch-btn:hover {
  background: #FEA116;
  color: #050E1A;
  box-shadow: 0 6px 20px rgba(254, 161, 22, 0.35);
  transform: translateY(-2px);
}

/* ════════════════════════════════════════════
   FORM SIDE PANEL
   ════════════════════════════════════════════ */
.hk-form-side {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 48px 44px;
  background: #FBFAF8;
  position: relative;
}

.hk-form-heading {
  font-family: 'Cinzel', serif;
  font-weight: 700;
  font-size: 1.6rem;
  color: #0B1D37;
  margin-bottom: 6px;
}

.hk-divider-gold {
  width: 36px;
  height: 3px;
  background: linear-gradient(90deg, #FEA116, #D4AF37);
  border-radius: 10px;
  margin-bottom: 24px;
}

/* ── Form elements ── */
.hk-auth-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.hk-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.hk-label {
  font-family: 'Poppins', sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  color: #3D4F6A;
}

.hk-input-box {
  position: relative;
  display: flex;
  align-items: center;
  background: #F0EDE8;
  border: 1.5px solid rgba(11, 29, 55, 0.08);
  border-radius: 14px;
  transition: all 0.3s ease;
  overflow: hidden;
}
.hk-input-box:focus-within {
  border-color: rgba(254, 161, 22, 0.5);
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(254, 161, 22, 0.08);
}

.hk-input-icon {
  position: absolute;
  left: 16px;
  color: #FEA116;
  font-size: 0.9rem;
  z-index: 1;
  pointer-events: none;
}

.hk-input {
  width: 100%;
  padding: 14px 48px 14px 44px;
  border: none;
  background: transparent;
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  color: #0B1D37;
  outline: none;
}
.hk-input::placeholder {
  color: #9CAABB;
}

/* Phone input variant */
.hk-country-code {
  flex-shrink: 0;
  padding: 0 0 0 14px;
  font-family: 'Poppins', sans-serif;
  font-size: 0.88rem;
  font-weight: 600;
  color: #3D4F6A;
  border-right: 1px solid rgba(11, 29, 55, 0.1);
  margin-right: 0;
  display: flex;
  align-items: center;
  height: 100%;
  padding-right: 10px;
}
.hk-input--phone {
  padding-left: 14px;
}
.hk-phone-icon {
  position: absolute;
  right: 16px;
  color: #3D4F6A;
  font-size: 0.9rem;
  opacity: 0.5;
}

/* Eye toggle button — styled colored circle */
.hk-eye-btn {
  position: absolute;
  right: 8px;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #0B2447, #1E3E72);
  border: none;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.25s ease;
  z-index: 2;
}
.hk-eye-btn:hover {
  background: linear-gradient(135deg, #FEA116, #D4AF37);
  color: #050E1A;
  transform: scale(1.05);
}

/* ── Submit button ── */
.hk-submit-btn {
  width: 100%;
  padding: 14px;
  border-radius: 14px;
  border: none;
  background: linear-gradient(135deg, #FEA116 0%, #D4AF37 100%);
  color: #050E1A;
  font-family: 'Poppins', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(254, 161, 22, 0.2);
}
.hk-submit-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #ff8c00, #FEA116);
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(254, 161, 22, 0.35);
}
.hk-submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Create account outline button */
.hk-create-btn {
  width: 100%;
  padding: 13px;
  border-radius: 14px;
  border: 1.5px solid rgba(11, 29, 55, 0.12);
  background: transparent;
  color: #3D4F6A;
  font-family: 'Poppins', sans-serif;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}
.hk-create-btn:hover {
  border-color: #FEA116;
  color: #FEA116;
  background: rgba(254, 161, 22, 0.04);
}

/* Footer switch link */
.hk-footer-switch {
  text-align: center;
  margin-top: 20px;
  font-family: 'Poppins', sans-serif;
  font-size: 0.84rem;
  color: #6B7A8D;
}
.hk-footer-switch button {
  background: none;
  border: none;
  color: #FEA116;
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.84rem;
}
.hk-footer-switch button:hover {
  color: #D4AF37;
}

/* Mobile inline switcher */
.hk-mobile-switch {
  text-align: center;
  margin-top: 16px;
  font-family: 'Poppins', sans-serif;
  font-size: 0.84rem;
  color: #6B7A8D;
}
.hk-mobile-switch button {
  background: none;
  border: none;
  color: #FEA116;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.84rem;
}

/* ── Alerts ── */
.hk-alert {
  padding: 10px 14px;
  border-radius: 10px;
  font-family: 'Poppins', sans-serif;
  font-size: 0.8rem;
  font-weight: 500;
  line-height: 1.4;
  margin-bottom: 8px;
}
.hk-alert-danger {
  background: rgba(231, 76, 60, 0.08);
  border: 1px solid rgba(231, 76, 60, 0.2);
  color: #c0392b;
}
.hk-alert-success {
  background: rgba(46, 204, 113, 0.08);
  border: 1px solid rgba(46, 204, 113, 0.2);
  color: #27ae60;
}

/* ════════════════════════════════════════════
   ADMIN CARD (centered single-panel)
   ════════════════════════════════════════════ */
.hk-admin-card {
  max-width: 430px;
  width: 100%;
  margin: 0 auto;
  background: linear-gradient(160deg, #0D2A54 0%, #091A38 50%, #050E1A 100%);
  border-radius: 28px;
  padding: 40px 36px;
  text-align: center;
  color: #FAF6F0;
  border: 1.5px solid rgba(212, 175, 55, 0.35);
  box-shadow: 0 30px 80px rgba(5, 14, 26, 0.4);
}

.hk-admin-lock-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: rgba(254, 161, 22, 0.12);
  border: 1.5px solid rgba(254, 161, 22, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: #FEA116;
  font-size: 1.4rem;
}

.hk-admin-heading {
  font-family: 'Cinzel', serif;
  font-weight: 700;
  color: #FEA116;
  font-size: 1.4rem;
  letter-spacing: 1px;
  margin-bottom: 6px;
}

.hk-admin-sub {
  font-family: 'Poppins', sans-serif;
  font-size: 0.82rem;
  color: rgba(250, 246, 240, 0.5);
  margin-bottom: 20px;
}

.hk-admin-card .hk-divider-gold {
  margin: 0 auto 24px;
}

.hk-admin-card .hk-auth-form {
  text-align: left;
}

.hk-admin-card .hk-label {
  color: rgba(250, 246, 240, 0.7);
}

.hk-admin-card .hk-input-box {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.12);
}
.hk-admin-card .hk-input-box:focus-within {
  border-color: rgba(254, 161, 22, 0.5);
  background: rgba(255, 255, 255, 0.1);
  box-shadow: 0 0 0 3px rgba(254, 161, 22, 0.1);
}

.hk-admin-card .hk-input {
  color: #FAF6F0;
}
.hk-admin-card .hk-input::placeholder {
  color: rgba(250, 246, 240, 0.35);
}

.hk-admin-card .hk-eye-btn {
  background: rgba(254, 161, 22, 0.2);
  border: 1px solid rgba(254, 161, 22, 0.3);
  color: #FEA116;
}

/* ════════════════════════════════════════════
   RESPONSIVE
   ════════════════════════════════════════════ */
@media (max-width: 767.98px) {
  .hk-auth-page {
    padding: 20px 12px;
    align-items: flex-start;
    padding-top: 100px;
  }

  .hk-split-card {
    flex-direction: column;
    min-height: auto;
    border-radius: 22px;
  }

  .hk-brand-bubble {
    flex: none;
    max-width: 100%;
    width: 100%;
    border-radius: 22px 22px 0 0 !important;
    padding: 30px 24px 26px;
    min-height: auto;
  }

  .hk-brand-center {
    padding: 16px 0;
  }

  .hk-brand-title {
    font-size: 1.4rem;
  }

  .hk-brand-desc {
    font-size: 0.8rem;
  }

  .hk-form-side {
    padding: 28px 22px;
  }

  .hk-form-heading {
    font-size: 1.35rem;
  }

  .hk-back-arrow {
    position: relative;
    top: auto;
    left: auto;
    margin-bottom: 12px;
    align-self: flex-start;
  }
}

@media (min-width: 768px) {
  .hk-mobile-switch {
    display: none !important;
  }
}
`;
